import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IEnhancedMenuItem } from '@components/table/types';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import { premiumDetailsHeaders } from '@features/production/proposal/constants';
import { IListingData } from '@models/listing';

import { useDownloadAmendmentPremiumDetails } from '../hooks';

type Props = {
  currencySymbol: string;
  status: string;
  amendmentId: string;
  lineCode: string;
  isGroupMedical?: boolean;
  isCertificate?: boolean;
  fetchData: (
    pageNumber?: number,
    keyword?: string,
    pageSize?: number,
    order?: 'desc' | 'asc',
    orderBy?: string
  ) => Promise<{
    pagedItems: any[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  }>;
};

const AmendmentPremiumDetailsAtom: React.FC<Props> = ({
  currencySymbol,
  status,
  amendmentId,
  lineCode,
  isGroupMedical = false,
  isCertificate = false,
  fetchData
}) => {
  const downloadPremiumDetails = useDownloadAmendmentPremiumDetails(
    lineCode,
    isCertificate ? 'EC11' : undefined
  );

  const handleDownloadPremiumDetails = useCallback(async () => {
    await downloadPremiumDetails({ amendmentId });
  }, [amendmentId, downloadPremiumDetails]);
  const renderCountRef = useRef(0);
  const fetchDataCallCountRef = useRef(0);
  const hasLoadedInitialDataRef = useRef(false);

  useEffect(() => {
    renderCountRef.current++;
  });

  const [tableData, setTableData] = useState<IListingData<any>>({
    pagedItems: [],
    pageNumber: 1,
    pageSize: 10,
    totalCount: 0
  });

  const [, setIsLoading] = useState(false);

  const onPageChange = useCallback(
    async (
      page: number,
      searchKey?: string,
      rowsPerPage?: number,
      order?: 'desc' | 'asc',
      orderBy?: string
    ): Promise<void> => {
      setIsLoading(true);
      try {
        const data = await fetchData(
          page,
          searchKey,
          rowsPerPage,
          order,
          orderBy
        );
        setTableData(data);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchData]
  );

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) {
      return;
    }

    fetchDataCallCountRef.current++;

    const loadInitialData = async () => {
      setIsLoading(true);

      try {
        const data = await fetchData();
        setTableData(data);
        hasLoadedInitialDataRef.current = true;
      } catch (error) {
        void error;
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const fetchDataRefCountRef = useRef(0);
  useEffect(() => {
    fetchDataRefCountRef.current++;
  }, [fetchData]);

  const actions: IEnhancedMenuItem[] =
    status !== 'NEW'
      ? [
          {
            name: 'download',
            title: 'Download',
            onClick: () => handleDownloadPremiumDetails(),
            rowIndependant: true
          }
        ]
      : [];

  return (
    <ListingTableWithPagination
      name="amendmentPremiumDetails"
      headers={premiumDetailsHeaders(
        currencySymbol,
        isGroupMedical,
        isCertificate,
        true
      )}
      data={tableData}
      onPageChange={onPageChange}
      disableSelection
      defaultOrderByColumn={isGroupMedical ? 'insured' : 'policyPerson.relation'}
      orderByAscendingByDefault
      inlineTitle={isGroupMedical ? 'Group Premium Details' : 'Premium Details'}
      inline
      actions={actions}
    />
  );
};

export { AmendmentPremiumDetailsAtom };
